import { TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Visibility, VisibilityOff, CheckCircle, Error, Warning } from '@mui/icons-material';
import { useEmailValidation } from '../../Hooks/useEmailValidation';
import styles from './InputCmp.module.css';

const InputCmpWithEmailValidation = ({
    label,
    onChange,
    variant,
    style,
    placeholder,
    type,
    name,
    control,
    rules,
    formatValue,
    startIcon,
    enableEmailValidation = false,
    validationDelay = 10,
    onValidationChange, // New prop for callback
    ...props
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [localValue, setLocalValue] = useState('');
    const { validateEmail, isValid, message, isLoading, clearValidation } = useEmailValidation();

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    // Notify parent component when validation state changes
    useEffect(() => {
        if (onValidationChange) {
            onValidationChange({ isValid, message, isLoading });
        }
    }, [isValid, message, isLoading, onValidationChange]);

    useEffect(() => {
        if (enableEmailValidation && type === 'email') {
            const timeoutId = setTimeout(() => {
                if (localValue && localValue.trim() !== '') {
                    validateEmail(localValue);
                } else {
                    clearValidation();
                }
            }, validationDelay);

            return () => clearTimeout(timeoutId);
        }
    }, [localValue, enableEmailValidation, type, validateEmail, clearValidation, validationDelay]);

    const getValidationIcon = () => {
        if (isLoading) {
            return <CircularProgress size={20} />;
        }
        
        if (isValid === true) {
            return <CheckCircle color="success" />;
        } else if (isValid === false) {
            return <Error color="error" />;
        }
        
        return null;
    };

    const getHelperText = (error) => {
        if (error) {
            return error.message;
        }
        
        if (enableEmailValidation && type === 'email' && message) {
            return message;
        }
        
        return null;
    };

    const hasError = (error) => {
        if (error) return true;
        if (enableEmailValidation && type === 'email' && isValid === false) {
            return true;
        }
        return false;
    };

    return (
        <Controller
            name={name}
            rules={rules}
            control={control ? control : 'test'}
            render={({
                field: { onChange: fieldOnChange, value },
                fieldState: { error },
            }) => (
                <TextField
                    label={label ? label : 'Outlined'}
                    variant={variant ? variant : 'outlined'}
                    onChange={(e) => {
                        let v = e.target.value;
                        if (formatValue) v = formatValue(v);
                        
                        if (enableEmailValidation && type === 'email') {
                            setLocalValue(v);
                        }
                        
                        fieldOnChange(v);
                        if (onChange) onChange(e);
                    }}
                    placeholder={placeholder}
                    sx={{ width: '100%', ...style }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
                    className={styles.inputBox + " " + props?.className}
                    value={value}
                    error={hasError(error)}
                    helperText={getHelperText(error)}
                    InputProps={{
                        startAdornment: startIcon ? (
                            <InputAdornment position="start">
                                {startIcon}
                            </InputAdornment>
                        ) : undefined,
                        endAdornment: (
                            <InputAdornment position="end">
                                {type === 'password' ? (
                                    <IconButton onClick={togglePasswordVisibility} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                ) : enableEmailValidation && type === 'email' ? (
                                    getValidationIcon()
                                ) : null}
                            </InputAdornment>
                        ),
                    }}
                    {...props}
                />
            )}
        />
    );
};

export default InputCmpWithEmailValidation; 